import styled from "@emotion/styled";
import {
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import {
  getFilesFileIDDownloadUrl,
  JobClipType,
  type JobDetailClipListItemDto,
  JobStatus,
} from "Infrastructure/Api/Api";
import { isSuccessResponse } from "Infrastructure/Api/ApiUtils";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useRetryFinalizationMutation } from "Projects/Api/Mutations/useRetryFinalizationMutation";
import { useSubmitJobMutation } from "Projects/Api/Mutations/useSubmitJobMutation";
import { AudioPlayer } from "Projects/Components/AudioPlayer";
import { useScriptEditor } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { BlButton } from "Shared/Buttons/BlButton";
import { Price } from "Shared/Price/Price";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";

type Props = {
  projectID: string;
  jobID: string;
  jobStatus: JobStatus;
  clips: JobDetailClipListItemDto[];
};

const ClipCard = styled("div")`
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.spacing(1)};
  padding: ${(props) => props.theme.spacing(Spacing.S, Spacing.M)};
`;

export const FinalizeTabContent = ({
  projectID,
  jobID,
  jobStatus,
  clips,
}: Props): React.ReactNode => {
  const { t } = useResource();
  const { isEditable } = useScriptEditor();
  const [selectedClipID, setSelectedClipID] = useState<string>("");
  const [clipUrls, setClipUrls] = useState<Record<string, string>>({});
  const [playingClipID, setPlayingClipID] = useState<string | null>(null);
  const finalizeMutation = useSubmitJobMutation(projectID, jobID);
  const retryFinalizationMutation = useRetryFinalizationMutation(
    projectID,
    jobID,
  );

  const finalClip = useMemo(
    () => clips.find((c) => c.type === JobClipType.Final) ?? null,
    [clips],
  );

  const isAlreadyFinalized = finalClip !== null;

  const availableClips = useMemo(
    () =>
      clips.filter(
        (c) => c.type === JobClipType.Full || c.type === JobClipType.Preview,
      ),
    [clips],
  );

  useEffect(() => {
    const clipsToFetch = [...availableClips, ...(finalClip ? [finalClip] : [])];
    for (const clip of clipsToFetch) {
      if (clipUrls[clip.fileID]) continue;

      getFilesFileIDDownloadUrl(clip.fileID).then((response) => {
        if (isSuccessResponse(response)) {
          setClipUrls((prev) => ({
            ...prev,
            [clip.fileID]: response.data.downloadUrl,
          }));
        }
      });
    }
  }, [availableClips, finalClip, clipUrls]);

  const handleFinalize = async () => {
    if (!selectedClipID) return;
    try {
      await finalizeMutation.mutateAsync({ jobClipID: selectedClipID });
    } catch {
      // error handled by mutation (auto-toast via processResponse)
    }
  };

  const handleRetryFinalization = () => {
    retryFinalizationMutation.mutate(undefined);
  };

  if (isAlreadyFinalized && finalClip) {
    const finalClipUrl = clipUrls[finalClip.fileID];
    return (
      <Stack spacing={Spacing.M}>
        <Typography variant="body2" color="text.secondary">
          {t(Resources.JobDetail.FinalClipReady)}
        </Typography>
        <ClipCard>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
            {formatDate(finalClip.dateCreated, DateFormat.DateTimeWithSeconds)}
          </Typography>
          {finalClipUrl && (
            <AudioPlayer
              src={finalClipUrl}
              isPlaying={playingClipID === finalClip.jobClipID}
              onPlay={() => setPlayingClipID(finalClip.jobClipID)}
            />
          )}
        </ClipCard>
        {finalClipUrl && (
          <BlButton
            color="primary"
            fullWidth
            onClick={() => window.open(finalClipUrl, "_blank")}
          >
            {t(Resources.JobDetail.DownloadFinalClip)}
          </BlButton>
        )}
      </Stack>
    );
  }

  if (jobStatus === JobStatus.AwaitingFunds) {
    return (
      <Stack spacing={Spacing.M}>
        <Alert severity="warning">
          {t(Resources.JobDetail.AwaitingFundsMessage)}
        </Alert>

        <BlButton
          color="primary"
          fullWidth
          onClick={handleRetryFinalization}
          isLoading={retryFinalizationMutation.isPending}
        >
          {t(Resources.JobDetail.RetryFinalization)}
        </BlButton>
      </Stack>
    );
  }

  return (
    <Stack spacing={Spacing.M}>
      <Typography variant="body2" color="text.secondary">
        {t(Resources.JobDetail.SelectFinalClip)}
      </Typography>

      <RadioGroup
        value={selectedClipID}
        onChange={(e) => setSelectedClipID(e.target.value)}
      >
        <Stack spacing={Spacing.S}>
          {availableClips.map((clip, index) => (
            <ClipCard key={clip.jobClipID}>
              {clip.type === JobClipType.Full ? (
                <FormControlLabel
                  value={clip.jobClipID}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {t(Resources.JobDetail.Clip)} {index + 1} |{" "}
                      {formatDate(
                        clip.dateCreated,
                        DateFormat.DateTimeWithSeconds,
                      )}
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
              ) : (
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  {t(Resources.JobDetail.ClipPreview)} {index + 1} |{" "}
                  {formatDate(clip.dateCreated, DateFormat.DateTimeWithSeconds)}
                </Typography>
              )}
              {clipUrls[clip.fileID] && (
                <AudioPlayer
                  src={clipUrls[clip.fileID]}
                  isPlaying={playingClipID === clip.jobClipID}
                  onPlay={() => setPlayingClipID(clip.jobClipID)}
                />
              )}
            </ClipCard>
          ))}
        </Stack>
      </RadioGroup>

      {selectedClipID &&
        (() => {
          const clip = availableClips.find(
            (c) => c.jobClipID === selectedClipID,
          );
          if (!clip) return null;
          return (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {t(Resources.JobDetail.ClipPrice)}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                <Price amount={clip.price} />
              </Typography>
            </Stack>
          );
        })()}

      <BlButton
        color="primary"
        fullWidth
        onClick={handleFinalize}
        disabled={!selectedClipID || !isEditable}
        isLoading={finalizeMutation.isPending}
      >
        {t(Resources.JobDetail.FinalizeConfirm)}
      </BlButton>
    </Stack>
  );
};
